import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import clone from 'clone';
import qs from 'qs';
import {
  Button,
  Divider,
  Form,
  Grid,
  Icon,
  Loader,
  Segment,
  Table,
} from 'semantic-ui-react';
import { required, stringLength, identity as ValidateIdentity, phoneNumber, matchRegex } from '@mhub/web-validations';
import { withTranslation } from 'react-i18next';
import { fetchM } from '../../actions/fetchM';
import config from '../../../config';
import { flashErrorMessage, flashMessage } from '../../actions/flash';
import { getPhoneValidRegex } from '../../utils/validations/phone';
import INTL_CALL_CODES from '../../constants/intlCallCodes';
import AppForm from '../../components/AppForm';
import ListItem from '../../components/ListItem';
import PhoneInput from '../../components/AppForm/PhoneInput';
import Select from '../../components/AppForm/Select';
import ProjectsList from '../modals/ProjectsList';
import SpecificationTable from '../../components/SpecificationTable';
import { isForeignCompany } from '../../utils/companies';
import { getItem } from '../../utils/storage';
import INTL_FLAG_CODES from '../../constants/intlFlagCodes';
import { devGroups, ignoreGroupIDs } from '../../constants/groups';
import { roleOptions } from '../../constants/roles';
import TinFieldInput from '../../components/AppForm/TinFieldInput';
import { REGEX_TIN_VALID, REGEX_NPWP_ID, REGEX_FULL_NAME, REGEX_BANK_ACCOUNT_NUMBER } from '../../constants/regex';
import { getIdentityTypeOpts } from '../../utils/identityTypes';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';
import SelectBank from '../../components/AppForm/SelectBank';
import { translateModels } from '../../utils/translation';
import { GENERIC_TIN_UNMATCHED_ERROR_MSG, GENERIC_TIN_VALUES, getGenericTinByIdTypeAndTinNo, GOVERNMENT_GENERIC_TIN, isGenericTinFound } from '../../constants/tin';
import { IDENTITY_TYPE_NRIC } from '../../constants/defaultIdentityTypeOpts';

class StaffForm extends Component {
  static propTypes = {
    onSubmit: PropTypes.func.isRequired,
    onChange: PropTypes.func,
    auth: PropTypes.shape({
      companyBranch: PropTypes.shape({
        id: PropTypes.number,
      }),
      isHeadquarters: PropTypes.bool,
      isSuperAdmin: PropTypes.bool,
      isBranchLeader: PropTypes.bool,
      company: PropTypes.shape({
        countryCode: PropTypes.string,
      }),
    }),
    t: PropTypes.func,
  }
  static contextTypes = {
    store: PropTypes.object,
  }
  static defaultStaff = {
    identityNo: '',
    taxIdentificationNo: '',
    taxTinValidationStatus: 'failed',
    name: '',
    preferredName: '',
    email: '',
    mobilePrefix: '60',
    mobile: '',
    officeTelPrefix: '60',
    officeTel: '',
    profileImageSrc: '',
    companyBranchID: '',
    viewAllProjects: false,
    affiliateProjects: [],
    userID: null,
    user: null,
    mappings: [],
    fullName: '',
    bankAccountName: '',
    bankAccountNumber: '',
  }
  static defaultUser = {
    id: null,
    groups: [],
  }
  constructor(props) {
    super(props);
    const staff = clone(StaffForm.defaultStaff);
    const tinError = null;
    const auth = props.auth;

    if (auth && isForeignCompany(auth.company)) {
      const idx = INTL_FLAG_CODES.findIndex(f => f.countryCode === auth.company.countryCode);
      if (idx !== -1) {
        const defaultCountryPhoneCode = INTL_FLAG_CODES[idx].value;
        staff.mobilePrefix = defaultCountryPhoneCode;
        staff.officeTelPrefix = defaultCountryPhoneCode;
      }
    }
    staff.user = clone(StaffForm.defaultUser);
    let phoneValidationPresets = {};
    const storePhoneValidationPresets = getItem(`${config.get('appName')}.phoneValidationPresets`);
    if (storePhoneValidationPresets) {
      try {
        phoneValidationPresets = JSON.parse(storePhoneValidationPresets).data || [];
      } catch (e) {
        Bugsnag.notifyException(e);
      }
    }

    this.state = {
      staff,
      oriStaff: clone(staff),
      errMessages: [],
      tinMessage: null,
      branches: [],
      companyBranches: [],
      selectedBranchIsExternal: false,
      showAddProject: false,
      phoneValidationPresets,
      tinError,
      tinEnabled: true,
      tinRequired: false,
      // flag to enable/disable tin searched using the lhdn api
      tinFound: false,
    };
    this.validation = {
      name: createRef(),
      preferredName: createRef(),
      email: createRef(),
      mobilePrefix: createRef(),
      mobile: createRef(),
      officeTel: createRef(),
      companyBranchID: createRef(),
      role: createRef(),
      identityNo: createRef(),
      taxIdentificationNo: createRef(),
      fullName: createRef(),
      bankAccountName: createRef(),
      bankAccountNumber: createRef(),
    };
  }
  componentDidMount() {
    const { store } = this.context;
    if (this.props.auth.isHeadquarters || this.props.auth.isSuperAdmin) {
      this.fetchCompanyBranches()
      .catch((err) => {
        Bugsnag.notifyException(err);
        store.dispatch(flashErrorMessage(err));
      });
    }
  }
  getRoles = () => {
    const rolesArr = [];
    if (this.state.staff.user.groups && this.state.staff.user.groups.length > 0) {
      this.state.staff.user.groups.forEach((g) => {
        // ignore CC & analytics role in SR
        if (ignoreGroupIDs.includes(g.id)) return;
        rolesArr.push(g.id);
      });
    }
    rolesArr.sort();
    return String(rolesArr.toString());
  }
  getBranchProjects = () => {
    const { staff, companyBranches } = this.state;
    if (staff && staff.companyBranchID && companyBranches.length > 0) {
      const foundBranch = companyBranches.find(d => d.id.toString() === staff.companyBranchID);
      if (foundBranch && foundBranch.projects && foundBranch.projects.length > 0) {
        return foundBranch.projects.filter(d => d.statusID !== 'deleted');
      }
    }
    return [];
  }
  getBranchViewAllProjects = () => {
    const { staff, companyBranches } = this.state;
    if (staff && staff.companyBranchID) {
      const foundBranch = companyBranches.find(d => d.id.toString() === staff.companyBranchID);
      if (foundBranch && foundBranch.viewAllProjects) {
        return true;
      }
    }
    return false;
  }
  setLoading = (loading = false, loadingType) => new Promise((res) => {
    if (loadingType === 'validatingTIN') {
      this.setState({ validatingTin: loading }, res);
    }
  })
  submit(url, opts) {
    return this.form.submit(url, opts);
  }
  handleChangeRole = (e, d) => {
    const { staff } = this.state;
    const groupIDs = d.value.split(',');
    let groups = devGroups.filter(g => groupIDs.includes(g.id));
    const untouchedGroups = staff.user.groups.filter(g => ignoreGroupIDs.includes(g.id));
    if (untouchedGroups && untouchedGroups.length > 0) {
      groups = [...groups, ...untouchedGroups];
    }

    staff.user.groups = groups;
    this.setState({ staff });
  }
  handleSubmit = (event) => {
    event.preventDefault();
    const { isHeadquarters, isSuperAdmin, companyBranch } = this.props.auth;
    const { staff } = this.state;
    if (!isHeadquarters && !isSuperAdmin && companyBranch && companyBranch.id) {
      staff.companyBranchID = companyBranch.id.toString();
    }
    if (staff && staff.mappings) {
      staff.mappings = staff.mappings.filter(d =>
        d.name.length > 0 && d.value.length > 0).map((d, idx) => ({
          ...d,
          position: idx + 1,
        }));
    }
    // `role` is derived from user.groups rather than being a field on the staff
    // record, so hand the validator a synthetic value instead of polluting the
    // payload that gets POSTed.
    return AppForm.validate(this.validation, { ...staff, role: this.getRoles() })
      .then((validations) => {
        this.setState({
          errMessages: validations.filter(v => !v.isValid),
        });

        staff.taxTinValidationStatus = this.state.tinError === false ? 'success' : 'failed';

        if (this.state.errMessages.length === 0 && this.state.tinRequired && staff.taxTinValidationStatus !== 'success') {
          const { store } = this.context;
          if (staff.taxTinValidationStatus !== 'success') {
            store.dispatch(flashMessage({
              header: 'Invalid TIN',
              message: 'Tax Identification Number is invalid',
              icon: 'warning sign',
              type: 'error',
              autoHideDuration: 5000,
            }));
            return;
          }
        }

        this.props.onSubmit(validations, staff);
      });
  }
  handleCheckChanges() {
    if (this.props.onChange) {
      this.props.onChange(true);
    }
  }
  handleInputChange = (event, data) => {
    const { companyBranches, staff } = this.state;
    let selectedBranchIsExternal = this.state.selectedBranchIsExternal;
    if (data.name === 'companyBranchID') {
      const branch = companyBranches.find(b => b.id.toString() === data.value);
      if (branch) {
        selectedBranchIsExternal = branch.isExternal;
        staff.user.groups = [
          { id: '10', code: 'DEVELOPER_EXECUTIVE' },
        ];
      }
    }
    staff[data.name] = data.value;
    this.setState({ staff, selectedBranchIsExternal }, () => {
      // Validate TIN again on identityNo change.
      if (data.name === 'identityNo') {
        if (staff.taxIdentificationNo !== '') {
          this.handleTinInput({ target: { value: staff.taxIdentificationNo } });
        }
      }
      // Re-validate phone fields when prefix changes to clear stale validation state.
      if (data.name === 'mobilePrefix' && staff.mobile !== '') {
        this.validation.mobile.current.validate(staff.mobile);
      }
      if (data.name === 'officeTelPrefix' && staff.officeTel !== '') {
        this.validation.officeTel.current.validate(staff.officeTel);
      }
      this.handleCheckChanges();
    });
  }
  async validateTIN(tinInput) {
    const { identityNo } = this.state.staff;
    if (!tinInput || !identityNo) {
      return;
    }
    const idType = getIdentityTypeOpts().find(i => i.value === IDENTITY_TYPE_NRIC).tin_code;
    const query = Object.assign({
      tin: tinInput,
      idType,
      idValue: identityNo,
    });
    const { store } = this.context;
    try {
      const res = await store.dispatch(fetchM(`/tin/validate/?${qs.stringify(query)}`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': this.props.token,
          'X-Origin': this.state.origin,
        },
      }));

      if (res && res.ok) {
        this.setState({
          tinError: false,
          tinFound: true,
          tinMessage: null,
        });
      } else {
        const errorMessage = await res.text();
        this.setState({
          tinError: true,
          tinFound: false,
          tinMessage: errorMessage,
        });
        return;
      }
    } catch (err) {
      if (err.res) {
        const errorMessage = await err.res.text();
        this.setState({
          tinError: true,
          tinFound: false,
          tinMessage: errorMessage,
        });
      }
    }
  }
  handleTinInput = (e) => {
    const { staff } = this.state;
    const { auth } = this.props;

    const isIndonesia = auth && auth.company && auth.company.countryCode && auth.company.countryCode.toUpperCase() === 'ID';

    if (isIndonesia) {
      staff.taxIdentificationNo = e.target.value; // Don't uppercase NPWP
      this.setState({ staff }, () => {
        this.handleCheckChanges();
      });
      return;
    }

    // --- Malaysian Logic ---
    const tinInput = e.target.value.toUpperCase(); // Uppercase for MY TIN
    const tinFound = e.target.tinFound;
    const isAlphanumeric = /^[a-zA-Z0-9]*$/.test(tinInput);

    if (tinInput === '') {
      staff.taxIdentificationNo = '';
      this.setState({ staff, tinFound: false, tinError: false, tinMessage: null });
      return;
    }

    if (isAlphanumeric) {
      staff.taxIdentificationNo = tinInput;
      this.setState({ staff, tinFound, tinError: tinFound === false, tinMessage: tinFound ? null : this.state.tinMessage }, () => {
        this.handleCheckChanges();
      });

      if (tinFound) {
        return;
      }
      // 1~2 letter followed by numeric with length 9 - 11
      const matched = REGEX_TIN_VALID.test(tinInput);
      const genericTin = getGenericTinByIdTypeAndTinNo(IDENTITY_TYPE_NRIC, tinInput);
      this.setLoading(true, 'validatingTIN').then(() => {
        if (this.debounceTimeout) {
          clearTimeout(this.debounceTimeout);
        }

        this.debounceTimeout = setTimeout(() => {
          if (matched) {
            if (tinInput === GOVERNMENT_GENERIC_TIN) {
              this.setState({
                tinError: false,
                tinFound: true,
                tinMessage: null },
              () => {
                this.setLoading(false, 'validatingTIN');
              });
            } else if (GENERIC_TIN_VALUES.includes(tinInput)) {
              this.setState({
                tinError: !isGenericTinFound(genericTin),
                tinFound: isGenericTinFound(genericTin),
                tinMessage: !isGenericTinFound(genericTin) ? GENERIC_TIN_UNMATCHED_ERROR_MSG : null },
              () => {
                this.setLoading(false, 'validatingTIN');
              });
            } else {
              this.validateTIN(tinInput).finally(() => {
                this.setLoading(false, 'validatingTIN');
              });
            }
          } else {
            this.setState({ tinError: true }, () => {
              this.setLoading(false, 'validatingTIN');
            });
          }
        }, 3000);
      });
    }
  };
  handleMappingChange = (m) => {
    const { staff } = this.state;
    staff.mappings = m;
    this.setState({ staff }, () => {
      this.handleCheckChanges();
    });
  }
  handleCheckBoxChange = (event, d) => {
    const { staff } = this.state;
    staff[d.name] = !staff[d.name];
    this.setState({ staff });
  }
  handleRemoveProject = (index) => {
    if (this.state.edit) {
      const { staff } = this.state;
      staff.projects.splice(index, 1);
      this.setState({ staff });
    }
  }
  handleAddProject = (e, projects) => {
    e.preventDefault();
    const { staff } = this.state;
    staff.projects = projects;
    this.setState({
      staff,
      showAddProject: false,
    });
  }
  handleShowAddProject = (e) => {
    e.preventDefault();
    this.setState({
      showAddProject: true,
    });
  }
  handleCloseAddProject = (e) => {
    if (e) { e.preventDefault(); }
    this.setState({
      showAddProject: false,
    });
  }
  async fetchCompanyBranches() {
    const { store } = this.context;
    let res;

    const query = {
      page: 'all',
      getProjects: true,
      statusFilter: 'active',
    };
    const queryString = qs.stringify(query);

    try {
      res = await store.dispatch(fetchM(`${config.get('bookApiURL')}/company_branches/?${queryString}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: 'An error occurred, please refresh page' }));
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const branches = await res.json();
      await this.setState({
        branches: branches ? branches.map((ul) => {
          const branch = { text: ul.name, name: ul.shortName, value: ul.id.toString(), description: ul.isExternal ? 'Agency' : '' };
          return branch;
        }) : [],
        companyBranches: branches,
      });
    }
  }
  isUserType(groupID) {
    if (this.state.staff.user.groups) {
      return this.state.staff.user.groups.some(group => (
        group.id === groupID
      ));
    }
    return false;
  }
  render() {
    const { t } = this.props;
    const { staff } = this.state;
    const ROLES = roleOptions(this.state.selectedBranchIsExternal, this.props.auth.isBranchLeader);
    const tinContent = staff.taxIdentificationNo || this.state.tinRequired;
    const isMalaysia = this.props.auth && this.props.auth.company && this.props.auth.company.countryCode && this.props.auth.company.countryCode.toUpperCase() === 'MY';
    const isIndonesia = this.props.auth && this.props.auth.company && this.props.auth.company.countryCode && this.props.auth.company.countryCode.toUpperCase() === 'ID';
    let tinValidation = [];
    if (isIndonesia) {
      tinValidation = [
        {
          validate: matchRegex,
          option: { validRegex: REGEX_NPWP_ID },
        },
      ];
    } else if (isMalaysia) {
      // For Malaysia, validation is optional unless tinRequired is true
      const tinRules = [{
        validate: matchRegex,
        option: { validRegex: REGEX_TIN_VALID },
      }];
      if (this.state.tinRequired) {
        tinRules.unshift(required);
      }
      tinValidation = tinRules;
    }
    return (
      <AppForm
        onSubmit={this.handleSubmit}
        error={this.state.errMessages.length > 0}
        ref={(ref) => { this.form = ref; }}
      >
        <ProjectsList
          open={this.state.showAddProject}
          onClose={this.handleCloseAddProject}
          onConfirm={this.handleAddProject}
          onCheck={project => project.loanTrackerProjectID < 1}
          selected={staff ? staff.affiliateProjects : []}
          userBranchProjects={!this.getBranchViewAllProjects() ? this.getBranchProjects() : []}
        />
        <Grid columns="equal" stackable doubling>
          <Grid.Row>
            <Grid.Column>
              <Form.Group widths="equal">
                <AppForm.Input
                  label={`${translateModels(t, 'name', 'user')}*`}
                  validateLabel={translateModels(t, 'name', 'user')}
                  name="name"
                  value={this.state.staff.name}
                  onChange={this.handleInputChange}
                  ref={this.validation.name}
                  validation={[required]}
                />
                {
                  this.props.auth.isHeadquarters || this.props.auth.isSuperAdmin ?
                    <Select
                      validation={[required]}
                      ref={this.validation.companyBranchID}
                      onChange={this.handleInputChange}
                      validateLabel="Business Unit"
                      label={`${t('Business Unit')}*`}
                      name="companyBranchID"
                      text={this.state.staff.companyBranchID ? null : t('Select business unit')}
                      value={this.state.staff.companyBranchID}
                      options={this.state.branches}
                      disabled={this.state.branches.length === 0}
                    /> :
                  null
                }
              </Form.Group>
              <Form.Group widths="equal">
                <AppForm.Input
                  label={`${translateModels(t, 'preferredName', 'user')}*`}
                  validateLabel={translateModels(t, 'preferredName', 'user')}
                  name="preferredName"
                  value={this.state.staff.preferredName}
                  onChange={this.handleInputChange}
                  ref={this.validation.preferredName}
                  validation={[required, {
                    validate: stringLength,
                    option: {
                      max: 10,
                    },
                  }]}
                />
                <Select
                  name="role"
                  label={`${t('Access Role')}*`}
                  validateLabel={t('Access Role')}
                  validation={[required]}
                  ref={this.validation.role}
                  value={this.getRoles()}
                  onChange={this.handleChangeRole}
                  options={ROLES.map(role => ({
                    ...role,
                    text: t(role.text, { ns: TRANSLATION_FILES.constants }),
                  }))}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <Select
                  validation={[required]}
                  ref={this.validation.mobilePrefix}
                  onChange={this.handleInputChange}
                  validateLabel={translateModels(t, 'mobilePrefix', 'user')}
                  label={`${translateModels(t, 'mobilePrefix', 'user')}*`}
                  name="mobilePrefix"
                  value={this.state.staff.mobilePrefix}
                  options={INTL_CALL_CODES}
                />
                <PhoneInput
                  label={`${translateModels(t, 'mobile', 'user')}*`}
                  validateLabel={translateModels(t, 'mobile', 'user')}
                  type="tel"
                  name="mobile"
                  value={this.state.staff.mobile}
                  onChange={this.handleInputChange}
                  prefix={this.state.staff.mobilePrefix}
                  ref={this.validation.mobile}
                  validation={[required, {
                    validate: phoneNumber,
                    option: this.state.staff.mobilePrefix === 'other' ? {} : {
                      isMobile: true,
                      phonePrefix: this.state.staff.mobilePrefix,
                      validRegex: getPhoneValidRegex(
                        this.state.phoneValidationPresets,
                        this.state.staff.mobilePrefix,
                        'mobile',
                      ),
                    },
                  }]}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <Form.Select
                  label={translateModels(t, 'officeTelPrefix', 'user')}
                  name="officeTelPrefix"
                  value={this.state.staff.officeTelPrefix}
                  onChange={this.handleInputChange}
                  search
                  options={INTL_CALL_CODES}
                  noResultsMessage={t('No results found', { ns: TRANSLATION_FILES.translation })}
                />
                <PhoneInput
                  label={translateModels(t, 'officeTel', 'user')}
                  type="tel"
                  name="officeTel"
                  value={this.state.staff.officeTel}
                  onChange={this.handleInputChange}
                  prefix={this.state.staff.officeTelPrefix}
                  ref={this.validation.officeTel}
                  validation={[{
                    validate: phoneNumber,
                    option: this.state.staff.officeTelPrefix === 'other' ? {} : {
                      phonePrefix: this.state.staff.officeTelPrefix,
                      validRegex: getPhoneValidRegex(
                        this.state.phoneValidationPresets,
                        this.state.staff.officeTelPrefix,
                        'phone',
                      ),
                    },
                  }]}
                />
              </Form.Group>
              <Form.Group widths="equal" style={{ alignItems: 'center' }}>
                <Form.Field>
                  <AppForm.Input
                    label={`${translateModels(t, 'email', 'user')}*`}
                    validateLabel={translateModels(t, 'email', 'user')}
                    name="email"
                    value={this.state.staff.email}
                    onChange={this.handleInputChange}
                    ref={this.validation.email}
                    validation={[required]}
                  />
                </Form.Field>
                <Form.Field>
                  {this.getBranchViewAllProjects() ?
                      t('All Projects Access (Set by Business Unit)')
                    :
                      <Form.Checkbox
                        checked={staff.viewAllProjects}
                        label={translateModels(t, 'viewAllProjects', 'user')}
                        name="viewAllProjects"
                        onChange={this.handleCheckBoxChange}
                      />
                  }
                </Form.Field>
              </Form.Group>
              <Form.Group widths="equal">
                <AppForm.Input
                  label={`${translateModels(t, 'fullName', 'user')} ${isMalaysia && t('(as in MyKad)')}`}
                  validateLabel={translateModels(t, 'fullName', 'user')}
                  name="fullName"
                  value={this.state.staff.fullName}
                  onChange={this.handleInputChange}
                  ref={this.validation.fullName}
                  validation={
                    (this.state.staff.bankAccountName && this.state.staff.bankAccountNumber) ?
                      [required, { validate: matchRegex, option: { validRegex: REGEX_FULL_NAME } }] :
                      [{ validate: matchRegex, option: { validRegex: REGEX_FULL_NAME } }]
                  }
                />
                {isMalaysia && (
                  <Form.Field>
                    <AppForm.Input
                      name="identityNo"
                      validation={tinContent ?
                      [required,
                        {
                          validate: ValidateIdentity,
                          option: {
                            countryCode: 'MY',
                            identityType: 'ic_number',
                          },
                        },
                      ] :
                      [{
                        validate: ValidateIdentity,
                        option: {
                          countryCode: 'MY',
                          identityType: 'ic_number',
                        },
                      }]}
                      ref={this.validation.identityNo}
                      required={tinContent}
                      onChange={this.handleInputChange}
                      validateLabel="MyKad (NRIC)"
                      label="MyKad (NRIC)"
                      type="text"
                      value={staff.identityNo}
                    />
                  </Form.Field>
                )}
                {isIndonesia && (
                  <Form.Field>
                    <AppForm.Input
                      name="identityNo"
                      validation={tinContent ?
                      [required,
                        {
                          validate: matchRegex,
                          option: { validRegex: /^[a-zA-Z0-9_]*$/ },
                        },
                      ] :
                      [{
                        validate: matchRegex,
                        option: { validRegex: /^[a-zA-Z0-9_]*$/ },
                      }]}
                      ref={this.validation.identityNo}
                      required={tinContent}
                      onChange={this.handleInputChange}
                      validateLabel="KTP"
                      label="KTP"
                      type="text"
                      value={staff.identityNo}
                    />
                  </Form.Field>
                )}
                {(isMalaysia || isIndonesia) && (
                  <Form.Field>
                    <TinFieldInput
                      isIndonesia={isIndonesia}
                      value={staff.taxIdentificationNo}
                      errMessage={this.state.tinMessage}
                      isLoading={this.state.validatingTin}
                      tinError={isMalaysia && this.state.tinError}
                      required={this.state.tinRequired}
                      // readOnly={isMalaysia && this.state.tinFound}
                      ref={this.validation.taxIdentificationNo}
                      onChange={this.handleTinInput}
                      validation={tinValidation}
                      identityType={IDENTITY_TYPE_NRIC}
                      identityValue={staff.identityNo}
                    />
                  </Form.Field>
                )}
              </Form.Group>
              <Form.Group widths="equal">
                <SelectBank
                  label={translateModels(t, 'bankAccountName', 'user')}
                  country={this.props.auth && this.props.auth.company ? this.props.auth.company.countryCode : ''}
                  validateLabel={translateModels(t, 'bankAccountName', 'user')}
                  ref={this.validation.bankAccountName}
                  onChange={this.handleInputChange}
                  name="bankAccountName"
                  placeholder={t('Select {{title}}', { title: t('Bank') })}
                  value={this.state.staff.bankAccountName}
                  validation={this.state.staff.bankAccountNumber ? [required] : []}
                />
                <AppForm.Input
                  label={translateModels(t, 'bankAccountNumber', 'user')}
                  validateLabel={translateModels(t, 'bankAccountNumber', 'user')}
                  name="bankAccountNumber"
                  value={this.state.staff.bankAccountNumber}
                  onChange={this.handleInputChange}
                  ref={this.validation.bankAccountNumber}
                  validation={this.state.staff.bankAccountName ?
                    [required, { validate: matchRegex, option: { validRegex: REGEX_BANK_ACCOUNT_NUMBER } }] :
                    [{ validate: matchRegex, option: { validRegex: REGEX_BANK_ACCOUNT_NUMBER } }]
                  }
                />
              </Form.Group>
              {
                this.props.auth.isSuperAdmin ?
                  <Form.Group grouped>
                    <Form.Field label={t('Access')} />
                    {devGroups.map((group, idx) => (
                      <Form.Checkbox
                        checked={this.isUserType(group.id)}
                        key={idx}
                        label={group.name}
                        value={group.code}
                        name={group.code}
                        disabled
                      />
                    ))}
                  </Form.Group> :
                  null
              }
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <Divider horizontal>{t('User API')}</Divider>
              {
                !this.state.loading && (
                  <SpecificationTable
                    type="affiliateMappings"
                    editable
                    isEditing
                    hideButton
                    notFoundNode={<p>{t('No mapping set')}.</p>}
                    headerNode={t('User Mapping')}
                    data={staff && staff.mappings ? staff.mappings : []}
                    onChange={this.handleMappingChange}
                    loading={this.state.loading}
                  />
                )
              }
            </Grid.Column>
          </Grid.Row>
          {
            staff && !staff.viewAllProjects && !this.getBranchViewAllProjects() ? (
              <Grid.Row>
                <Grid.Column>
                  <Divider horizontal>{t('Projects Access')}</Divider>
                  <Table unstackable>
                    <Table.Body>
                      { !this.getBranchViewAllProjects() && this.getBranchProjects().length > 0 ?
                        this.getBranchProjects().map((project, idx) => (
                          <Table.Row key={idx}>
                            <Table.Cell>
                              <ListItem
                                item={project}
                                header={item => <span>{item.name} <Icon name="eye" fitted /><i> <small>Business Unit Access</small></i></span>}
                                subheader={item => item.status.name}
                              />
                            </Table.Cell>
                          </Table.Row>
                        ))
                        : null
                      }
                      { staff.projects && staff.projects.length > 0 &&
                        staff.projects.map((project, idx) => (
                          <Table.Row key={idx}>
                            <Table.Cell>
                              <ListItem
                                item={project}
                                header={item => item.name}
                                subheader={item => item.status.name}
                                rightComponent={
                                  <div className={this.state.edit ? null : 'hidden'}>
                                    <Button
                                      type="button"
                                      compact
                                      negative
                                      size="mini"
                                      icon="remove"
                                      onClick={() => this.handleRemoveProject(idx)}
                                      disabled={!this.state.edit}
                                    />
                                  </div>
                                }
                              />
                            </Table.Cell>
                          </Table.Row>
                        ))
                      }
                    </Table.Body>
                  </Table>
                  { !((staff.projects && staff.projects.length > 0) ||
                    (this.getBranchProjects() && this.getBranchProjects().length > 0)
                  ) &&
                    <Segment textAlign="center">
                      {
                        this.state.loading ?
                          <Loader active inline="centered">Loading</Loader>
                        : t('No projects access')
                      }
                    </Segment>
                  }
                </Grid.Column>
              </Grid.Row>
            ) : null
          }
          {
            staff && !staff.viewAllProjects ? (
              <Grid.Row>
                <Grid.Column textAlign="center">
                  <Button
                    content={t('Add project')}
                    onClick={this.handleShowAddProject}
                  />
                </Grid.Column>
              </Grid.Row>
            ) : null
          }
        </Grid>
      </AppForm>
    );
  }
}

export default withTranslation([
  ...namespaces,
  TRANSLATION_FILES.models.user,
], { withRef: true })(StaffForm);



// WEBPACK FOOTER //
// ./src/containers/Staffs/Form.jsx